import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Hero from '../components/Hero';
import SpecialsBlock from '../components/SpecialsBlock';
import { DEFAULT_SPECIALS, normalizeSpecials, cloneSpecials } from '../menuDefaults';

function Home() {
  // Monthly specials are managed from /admin and stored in Firestore (settings/specials).
  const [specials, setSpecials] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'settings', 'specials'))
      .then((snap) => {
        if (cancelled) return;
        setSpecials(snap.exists() ? normalizeSpecials(snap.data()) : cloneSpecials(DEFAULT_SPECIALS));
      })
      .catch((err) => {
        console.error('Failed to load specials', err);
        if (!cancelled) setSpecials(cloneSpecials(DEFAULT_SPECIALS));
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Animation on scroll
    const animateOnScroll = () => {
      const elements = document.querySelectorAll('.menu-item');
      
      elements.forEach(element => {
        const elementPosition = element.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        
        if (elementPosition < windowHeight - 50) {
          element.style.opacity = '1';
          element.style.transform = 'translateY(0)';
        }
      });
    };
    
    // Set initial state for animation elements
    const elements = document.querySelectorAll('.menu-item');
    elements.forEach(element => {
      element.style.opacity = '0';
      element.style.transform = 'translateY(20px)';
      element.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
    });
    
    // Run animation check on load and scroll
    animateOnScroll();
    window.addEventListener('scroll', animateOnScroll);
    
    return () => {
      window.removeEventListener('scroll', animateOnScroll);
    };
  }, []);

  const cloudName = specials && specials.cloudTopping ? specials.cloudTopping.name.trim() : '';

  return (
    <main>
      <Hero 
        title="GROUNDED COFFEE CO"
        tagline='ORGANIC COFFEE <span class="and">AND</span> TEA'
        showSocial={true}
      />

      <section className="menu-section">
        <h2>OUR MENU</h2>
        <p className="menu-note">One size only. 16 oz hot or iced.</p>
        <p className="menu-note">All espresso, teas, and syrups are organic</p>
        
        {specials ? (
          <SpecialsBlock specials={specials} className="specials-live" />
        ) : (
          <div className="menu-category specials-block specials-loading">
            <div className="schedule-loading"><div className="schedule-spinner" /></div>
          </div>
        )}

        <div className="menu-category">
          <h3>SPECIALTY DRINKS</h3>
          <div className="menu-items">
            <div className="menu-item">
              <div className="menu-item-name">Caramel Cloud Latte</div>
              <div className="menu-item-price">$7.50</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Tiramisu Latte</div>
              <div className="menu-item-price">$7.50</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Raspberry Matcha</div>
              <div className="menu-item-price">$7.90</div>
              <div className="menu-item-description">Ceremonial matcha + raspberry</div>
            </div>
          </div>
        </div>
        
        <div className="menu-category">
          <h3>COFFEE DRINKS</h3>
          <div className="menu-items">
            <div className="menu-item">
              <div className="menu-item-name">Latte</div>
              <div className="menu-item-price">Hot $5.75 / Iced $5.00</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Americano</div>
              <div className="menu-item-price">Hot $4.50 / Iced $4.00</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Mocha</div>
              <div className="menu-item-price">Hot $7.25 / Iced $7.00</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">White Mocha</div>
              <div className="menu-item-price">Hot $7.25 / Iced $7.00</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Hazelnut Truffle</div>
              <div className="menu-item-price">Hot $7.50 / Iced $7.25</div>
              <div className="menu-item-description">Dark chocolate + hazelnut + espresso</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Double Caramel Latte</div>
              <div className="menu-item-price">Hot $7.25 / Iced $7.00</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Cappuccino</div>
              <div className="menu-item-price">Hot $5.75</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Drip Coffee</div>
              <div className="menu-item-price">Hot $4.00 / Iced $3.50</div>
            </div>
          </div>
        </div>

        <div className="menu-category">
          <h3>NOT COFFEE</h3>
          <p className="category-note">All teas organic</p>
          <div className="menu-items">
            <div className="menu-item">
              <div className="menu-item-name">Matcha Latte</div>
              <div className="menu-item-price">Hot $7.50 / Iced $7.00</div>
              <div className="menu-item-description">Always hand whisked, Always ceremonial grade</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Cran-Raspberry Refresher</div>
              <div className="menu-item-price">Iced $5.00</div>
              <div className="menu-item-description">Cran"berry" tea + raspberry</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Peach Paradise Refresher</div>
              <div className="menu-item-price">$5.00</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Chai</div>
              <div className="menu-item-price">Hot $5.00 / Iced $4.50</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Black Tea</div>
              <div className="menu-item-price">Hot $3.00 / Iced $3.00</div>
              <div className="menu-item-description">Unsweet</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Berry Tea</div>
              <div className="menu-item-price">Hot $3.00 / Iced $3.00</div>
              <div className="menu-item-description">Unsweet</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Hot Chocolate</div>
              <div className="menu-item-price">Hot $4.50</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Chocolate Milk</div>
              <div className="menu-item-price">Iced $4.25</div>
            </div>
          </div>
        </div>

        <div className="menu-category-row">
          <div className="menu-category half-width">
            <h3>MILK OPTIONS</h3>
            <div className="menu-items">
              <div className="menu-item">
                <div className="menu-item-name">Whole Milk</div>
                <div className="menu-item-price">$0.00</div>
              </div>
              <div className="menu-item">
                <div className="menu-item-name">Oat Milk</div>
                <div className="menu-item-price">$0.75</div>
              </div>
              <div className="menu-item">
                <div className="menu-item-name">Almond</div>
                <div className="menu-item-price">$0.75</div>
              </div>
            </div>
          </div>

          <div className="menu-category half-width">
            <h3>ADD ONS</h3>
            <div className="menu-items">
              <div className="menu-item">
                <div className="menu-item-name">Extra Shot</div>
                <div className="menu-item-price">1 shot $1.00 / 2 shots $1.50 / 3 shots $2.00 / 4 shots $2.50</div>
              </div>
              <div className="menu-item">
                <div className="menu-item-name">Drizzle</div>
                <div className="menu-item-price">$0.90</div>
                <div className="menu-item-description">Caramel, mocha, white mocha</div>
              </div>
              <div className="menu-item">
                <div className="menu-item-name">Cloud Topping</div>
                <div className="menu-item-price">$1.50</div>
                {cloudName && <div className="menu-item-description">This month: {cloudName}</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="menu-category">
          <h3>SYRUPS</h3>
          <p className="category-note">All syrups organic</p>
          <div className="menu-items">
            <div className="menu-item">
              <div className="menu-item-name">Vanilla</div>
              <div className="menu-item-price">$0.90</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Caramel</div>
              <div className="menu-item-price">$0.90</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Hazelnut</div>
              <div className="menu-item-price">$0.90</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Raspberry</div>
              <div className="menu-item-price">$0.90</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Brown Sugar</div>
              <div className="menu-item-price">$0.90</div>
            </div>
            <div className="menu-item">
              <div className="menu-item-name">Honey</div>
              <div className="menu-item-price">$0.90</div>
            </div>
          </div>
        </div>

      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2>ORDER AHEAD</h2>
          <p>Skip the line and order your favorite drinks ahead of time!</p>
          <button className="cta-button">Coming Soon</button>
        </div>
      </section>

      {/* Instagram Feed Section */}
      <section className="instagram-section">
        <div className="instagram-header">
          <h2>FOLLOW THE JOURNEY</h2>
          <p>See what we're brewing on Instagram</p>
        </div>
        <div className="instagram-feed">
          <div className="instagram-grid">
            {/* These will link to Instagram - styled as a preview grid */}
            <a href="https://instagram.com/groundedcoffee_co" target="_blank" rel="noopener noreferrer" className="instagram-tile">
              <div className="instagram-tile-overlay">
                <i className="fab fa-instagram"></i>
              </div>
              <div className="instagram-tile-placeholder tile-1"></div>
            </a>
            <a href="https://instagram.com/groundedcoffee_co" target="_blank" rel="noopener noreferrer" className="instagram-tile">
              <div className="instagram-tile-overlay">
                <i className="fab fa-instagram"></i>
              </div>
              <div className="instagram-tile-placeholder tile-2"></div>
            </a>
            <a href="https://instagram.com/groundedcoffee_co" target="_blank" rel="noopener noreferrer" className="instagram-tile">
              <div className="instagram-tile-overlay">
                <i className="fab fa-instagram"></i>
              </div>
              <div className="instagram-tile-placeholder tile-3"></div>
            </a>
            <a href="https://instagram.com/groundedcoffee_co" target="_blank" rel="noopener noreferrer" className="instagram-tile">
              <div className="instagram-tile-overlay">
                <i className="fab fa-instagram"></i>
              </div>
              <div className="instagram-tile-placeholder tile-4"></div>
            </a>
            <a href="https://instagram.com/groundedcoffee_co" target="_blank" rel="noopener noreferrer" className="instagram-tile">
              <div className="instagram-tile-overlay">
                <i className="fab fa-instagram"></i>
              </div>
              <div className="instagram-tile-placeholder tile-5"></div>
            </a>
            <a href="https://instagram.com/groundedcoffee_co" target="_blank" rel="noopener noreferrer" className="instagram-tile">
              <div className="instagram-tile-overlay">
                <i className="fab fa-instagram"></i>
              </div>
              <div className="instagram-tile-placeholder tile-6"></div>
            </a>
          </div>
        </div>
        <a href="https://instagram.com/groundedcoffee_co" target="_blank" rel="noopener noreferrer" className="instagram-follow-btn">
          <i className="fab fa-instagram"></i>
          <span>@groundedcoffee_co</span>
        </a>
      </section>
    </main>
  );
}

export default Home;

